'use client';

import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { VelocityTheme } from '../../environmentVariables/EnvironmentVariables';
import useCurrentSettings from '../../hooks/useCurrentSettings';
import useVelocityTheme from '../../hooks/useVelocityTheme';
import { cm } from 'src/utils/classNames';

const ValueChangeBlink = (
	props: PropsWithChildren<{ value: any; className?: string }>
) => {
	const [{ blinkOnValueChange: blinkEnabled }] = useCurrentSettings();

	const [flipBrightness, setFlipBrightness] = useState(false);

	const flipping = useRef(false);
	// Held in a ref, not state: updating it must not re-run the effect below, or
	// the effect's own cleanup would cancel the blink it just started.
	const prevValue = useRef(props.value);
	const blinkTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
		undefined
	);

	// Only unmount clears a pending blink. Values changing mid-blink are throttled
	// by `flipping` instead of restarting the animation.
	useEffect(() => () => clearTimeout(blinkTimeout.current), []);

	useEffect(() => {
		if (!blinkEnabled) return;
		if (props.value === prevValue.current) return;

		prevValue.current = props.value;
		if (flipping.current) return;

		flipping.current = true;
		setFlipBrightness(true);
		blinkTimeout.current = setTimeout(() => {
			setFlipBrightness(false);
			flipping.current = false;
		}, 150);
	}, [props.value, blinkEnabled]);

	const isLightTheme = useVelocityTheme() === VelocityTheme.light;

	return (
		<span
			className={cm(
				`transition-all ${
					flipBrightness
						? `brightness-50 ${isLightTheme ? 'opacity-30' : 'opacity-50'}`
						: 'opacity-100 brightness-100'
				}`,
				props.className
			)}
		>
			{props.children}
		</span>
	);
};

export default ValueChangeBlink;
